/**
 * The three handles on a curve.
 *
 * An arc is stored as a centre, a radius and two angles, and none of those is
 * what you want to take hold of. The original offers three grips instead, and
 * they are genuinely different operations rather than three names for a move:
 *
 *   · **Origin** (`G G`) picks the whole curve up by its *centre*, so it can be
 *     put down with its centre on a pad or a grid point. Grab Arc already moves
 *     one; what this adds is where the cursor takes hold.
 *   · **End point** (`G D`) swings one end round, leaving the other alone.
 *   · **Centre point** (`G V`) moves the centre while both ends stay put, which
 *     is how you make an arc flatter or rounder between two fixed points.
 *
 * The last is the one with real geometry in it. Two fixed ends and a centre are
 * over-determined — a circle through both ends has its centre on their
 * perpendicular bisector and nowhere else — so the cursor is projected onto
 * that line. Dragging sideways off it does nothing, which is correct and reads
 * correctly: the arc bows and does not slide.
 *
 * Every handle grabs the same way the original describes: point at the curve's
 * edge, click, and the cursor jumps to the handle before anything moves.
 */

import type { ArcEntity } from '@sircad/core/model.ts';
import type { Point } from '../render/viewport.ts';
import type { Tool, ToolContext } from './tool.ts';
import { hitTest } from './hit.ts';

export type ArcHandle = 'origin' | 'end' | 'centre';

const LABELS: Record<ArcHandle, { label: string; hint: string }> = {
  origin: {
    label: 'Arc Origin',
    hint: 'Click a curve to lift it by its centre, click again to set it down, Esc to cancel',
  },
  end: {
    label: 'Arc End Point',
    hint: 'Click a curve to swing its nearest end, click again to set it, Esc to cancel',
  },
  centre: {
    label: 'Curve Centre',
    hint: 'Click a curve to bow it between its ends, click again to set it, Esc to cancel',
  },
};

const RAD = Math.PI / 180;

/** Where an arc's two ends sit. */
function ends(a: ArcEntity): { start: Point; finish: Point } {
  const s = a.angle * RAD;
  const f = (a.angle + a.sweep) * RAD;
  return {
    start: { x: a.cx + a.radius * Math.cos(s), y: a.cy + a.radius * Math.sin(s) },
    finish: { x: a.cx + a.radius * Math.cos(f), y: a.cy + a.radius * Math.sin(f) },
  };
}

/** Degrees CCW from +X, always 0..360. */
function bearing(cx: number, cy: number, x: number, y: number): number {
  const d = (Math.atan2(y - cy, x - cx) / RAD) % 360;
  return d < 0 ? d + 360 : d;
}

export class ArcHandleTool implements Tool {
  readonly id: string;
  readonly label: string;
  readonly key: string;
  readonly hint: string;
  readonly cursor = 'crosshair';

  private readonly handle: ArcHandle;
  /** The curve being worked on, and what it looked like before. */
  private arc: ArcEntity | null = null;
  private mark: number | null = null;
  /** Which end `end` took hold of: the start, or the far one. */
  private movingStart = true;
  /** The ends as they stood when the grab began, for the centre handle. */
  private fixed: { start: Point; finish: Point } | null = null;
  /** Where the whole curve sat, for the origin handle. */
  private from: Point | null = null;

  constructor(handle: ArcHandle, key: string) {
    this.handle = handle;
    this.id = `arc-${handle}`;
    this.label = LABELS[handle].label;
    this.hint = LABELS[handle].hint;
    this.key = key;
  }

  onPointerDown(p: Point, ctx: ToolContext): void {
    if (this.arc) { this.nail(ctx); return; }

    // On the working layer, as every other grab is: stacked copper otherwise
    // hands you a curve from a layer you cannot see.
    const hit = hitTest(ctx.doc(), p.x, p.y, ctx.grid || 0.01, ctx.activeLayer,
      (e) => e.kind === 'arc');
    if (!hit || hit.kind !== 'arc') {
      ctx.setMessage(`${this.label} — click on the edge of a curve`);
      return;
    }

    this.arc = hit;
    this.mark = ctx.editMark();
    ctx.beginEdit(this.label);

    const e = ends(hit);
    this.fixed = e;
    this.from = { x: hit.cx, y: hit.cy };
    // Whichever end was pointed at, so the grip is the one you reached for.
    this.movingStart = Math.hypot(p.x - e.start.x, p.y - e.start.y)
      <= Math.hypot(p.x - e.finish.x, p.y - e.finish.y);

    ctx.setMessage(`${this.label} — move it, click to set it, Esc to put it back`);
    ctx.redraw();
  }

  onPointerMove(p: Point, ctx: ToolContext): void {
    const a = this.arc;
    if (!a) return;
    const at = ctx.snap(p.x, p.y);
    this.apply(a, { x: at.x, y: at.y });
    ctx.redraw();
  }

  /** Move the handle to this point. */
  private apply(a: ArcEntity, to: Point): void {
    if (this.handle === 'origin') {
      // The whole curve, carried by its centre.
      a.cx = to.x;
      a.cy = to.y;
      return;
    }

    if (this.handle === 'end') {
      // Swing one end round the centre. The radius follows the cursor as well,
      // because an end you can only rotate is a handle that refuses to go where
      // you put it — and the other end has to keep its place, so the far
      // bearing is held and the sweep recomputed from it.
      const far = this.movingStart ? a.angle + a.sweep : a.angle;
      const now = bearing(a.cx, a.cy, to.x, to.y);
      const r = Math.hypot(to.x - a.cx, to.y - a.cy);
      if (r > 1e-9) a.radius = r;
      if (this.movingStart) {
        a.angle = now;
        a.sweep = shortestTo(far - now);
      } else {
        a.sweep = shortestTo(now - a.angle);
      }
      return;
    }

    // The centre, with both ends nailed down. A circle through two fixed points
    // has its centre on their perpendicular bisector, so the cursor is dropped
    // onto that line rather than taken where it is.
    const f = this.fixed;
    if (!f) return;
    const mx = (f.start.x + f.finish.x) / 2;
    const my = (f.start.y + f.finish.y) / 2;
    const dx = f.finish.x - f.start.x;
    const dy = f.finish.y - f.start.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return;               // a closed circle has no bisector
    const nx = -dy / len;
    const ny = dx / len;
    const t = (to.x - mx) * nx + (to.y - my) * ny;
    const cx = mx + nx * t;
    const cy = my + ny * t;

    a.cx = cx;
    a.cy = cy;
    a.radius = Math.hypot(f.start.x - cx, f.start.y - cy);
    a.angle = bearing(cx, cy, f.start.x, f.start.y);
    a.sweep = shortestTo(bearing(cx, cy, f.finish.x, f.finish.y) - a.angle);
  }

  private nail(ctx: ToolContext): void {
    if (!this.arc) return;
    this.arc = null;
    this.fixed = null;
    this.from = null;
    this.mark = null;
    ctx.commit();
    ctx.setMessage(`${this.label} — set`);
    ctx.redraw();
  }

  busy(): boolean {
    return this.arc !== null;
  }

  cancel(ctx: ToolContext): void {
    if (!this.arc) return;
    this.arc = null;
    this.fixed = null;
    this.from = null;
    if (this.mark !== null) ctx.revertTo(this.mark);
    this.mark = null;
    ctx.setMessage(`${this.label} — put back`);
    ctx.redraw();
  }
}

/**
 * The same turn, expressed as the shorter way round.
 *
 * A sweep is signed and may be either way about; normalising to (-180, 180]
 * keeps a nearly straight arc from flipping to the long way round the circle
 * when its end crosses the axis, which looks like the arc jumping inside out.
 */
function shortestTo(deg: number): number {
  let d = deg % 360;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}
